import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend,
} from 'recharts';
import { api } from '../api';
import type { MetricsRange } from '../api';
import { useDemo } from '../context/DemoContext';
import { DEMO_ADMIN_DASHBOARD } from '../demo/data';

// ── helpers ─────────────────────────────────────────────────────────────────

function formatCurrency(amount: number) {
  if (amount >= 1_000_000) return `$${(amount / 1_000_000).toFixed(1)}M`;
  if (amount >= 1_000) return `$${(amount / 1_000).toFixed(1)}K`;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(amount);
}

function formatDate(dateStr: string) {
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ── design tokens ────────────────────────────────────────────────────────────

const GOLD = '#FFD400';
const MINT = '#4ade80';
const ROYAL = '#a78bfa';
const DANGER = '#f87171';
const BLUE = '#38bdf8';
const ORANGE = '#fb923c';

const PIPELINE_CONFIG: Record<string, { label: string; color: string }> = {
  form_incomplete:    { label: 'Form Incomplete',   color: '#525252' },
  form_completed:     { label: 'Form Submitted',     color: BLUE },
  generating:         { label: 'Generating',         color: ROYAL },
  website_in_progress:{ label: 'In Progress',        color: ORANGE },
  draft_ready:        { label: 'Draft Ready',        color: GOLD },
  website_completed:  { label: 'Live',               color: MINT },
};

const PRODUCT_REQ_CONFIG: Record<string, { label: string; color: string }> = {
  submitted:                   { label: 'Submitted',       color: BLUE },
  in_review:                   { label: 'In Review',       color: ROYAL },
  approved_to_create_shopify:  { label: 'Approved',        color: GOLD },
  live:                        { label: 'Live',            color: MINT },
  rejected:                    { label: 'Rejected',        color: DANGER },
};

// ── sub-components ───────────────────────────────────────────────────────────

function KpiCard({
  label, value, sub, accent = GOLD, icon, loading,
}: {
  label: string; value: string | number; sub?: string; accent?: string; icon: React.ReactNode; loading: boolean;
}) {
  return (
    <div className="relative overflow-hidden rounded-2xl bg-ink-900 border border-ink-700/50 p-5 flex flex-col gap-3">
      <div className="flex items-start justify-between">
        <p className="text-ink-400 text-xs font-medium uppercase tracking-widest">{label}</p>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${accent}18` }}>
          <span style={{ color: accent }}>{icon}</span>
        </div>
      </div>
      {loading ? (
        <div className="h-9 w-28 rounded-lg bg-ink-700 animate-pulse" />
      ) : (
        <p className="text-3xl font-display font-bold text-ink-50 leading-none">{value}</p>
      )}
      {sub && !loading && <p className="text-xs text-ink-500">{sub}</p>}
      {/* subtle glow */}
      <div className="absolute -bottom-4 -right-4 w-16 h-16 rounded-full opacity-10 blur-2xl" style={{ background: accent }} />
    </div>
  );
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ name: string; value: number; color: string }>;
  label?: string;
}

function RevenueTooltip({ active, payload, label }: CustomTooltipProps) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-xl bg-ink-800 border border-ink-700 p-3 text-sm shadow-xl">
      <p className="text-ink-300 mb-2">{label ? formatDate(label) : ''}</p>
      {payload.map((p) => (
        <div key={p.name} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="text-ink-400">{p.name === 'revenue' ? 'Revenue' : 'Orders'}:</span>
          <span className="text-ink-100 font-medium ml-1">
            {p.name === 'revenue' ? formatCurrency(p.value) : p.value}
          </span>
        </div>
      ))}
    </div>
  );
}

function InfluencerTooltip({ active, payload }: CustomTooltipProps) {
  if (!active || !payload?.length) return null;
  const d = payload[0];
  return (
    <div className="rounded-xl bg-ink-800 border border-ink-700 p-3 text-sm shadow-xl">
      <p className="text-ink-100 font-medium mb-1">{d.name}</p>
      <p className="text-ink-400">Revenue: <span className="text-gold font-semibold">{formatCurrency(d.value)}</span></p>
    </div>
  );
}

function EmptyState({ text }: { text: string }) {
  return (
    <div className="flex flex-col items-center justify-center h-full gap-2 text-ink-500">
      <svg className="w-8 h-8 opacity-40" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
          d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
      <p className="text-sm">{text}</p>
    </div>
  );
}

// ── main component ───────────────────────────────────────────────────────────

export default function AdminDashboard() {
  const { isDemoMode } = useDemo();
  const [range, setRange] = useState<MetricsRange>('30d');
  const [data, setData] = useState<Awaited<ReturnType<typeof api.getDashboardAnalytics>> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isDemoMode) {
      setData(DEMO_ADMIN_DASHBOARD as Awaited<ReturnType<typeof api.getDashboardAnalytics>>);
      setLoading(false);
      setError('');
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError('');
    api.getDashboardAnalytics(range)
      .then((d) => { if (!cancelled) { setData(d); setLoading(false); } })
      .catch((err) => { if (!cancelled) { setError(err.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [range, isDemoMode]);

  const { summary, revenueByDay, websitePipeline, productRequests, topInfluencers } = data ?? {
    summary: { totalRevenue: 0, totalOrders: 0, totalInfluencers: 0, activeInfluencers: 0, avgOrderValue: 0, totalVendors: 0 },
    revenueByDay: [] as Array<{ date: string; revenue: number; orders: number }>,
    websitePipeline: {} as Record<string, number>,
    productRequests: {} as Record<string, number>,
    topInfluencers: [] as Array<{ influencer_id: number; name: string; total_orders: number; total_revenue: number }>,
  };

  // pipeline donut data
  const pipelineDonut = Object.entries(websitePipeline)
    .filter(([, v]) => v > 0)
    .map(([k, v]) => ({
      name: PIPELINE_CONFIG[k]?.label ?? k,
      value: v,
      color: PIPELINE_CONFIG[k]?.color ?? '#525252',
    }));

  // product requests bar data
  const productReqBars = Object.entries(productRequests)
    .map(([k, v]) => ({
      name: PRODUCT_REQ_CONFIG[k]?.label ?? k,
      value: v,
      color: PRODUCT_REQ_CONFIG[k]?.color ?? GOLD,
    }))
    .sort((a, b) => b.value - a.value);

  // influencer bar data
  const influencerBars = topInfluencers.map((inf) => ({
    name: inf.name ?? `Influencer ${inf.influencer_id}`,
    revenue: inf.total_revenue,
    orders: inf.total_orders,
  }));

  const rangeOptions: { label: string; value: MetricsRange }[] = [
    { label: '7D', value: '7d' },
    { label: '30D', value: '30d' },
    { label: '90D', value: '90d' },
    { label: 'All', value: 'all' },
  ];

  const totalSites = Object.values(websitePipeline).reduce((s, v) => s + v, 0);
  const liveSites = websitePipeline['website_completed'] ?? 0;

  return (
    <div className="space-y-6 pb-10">

      {/* ── Page header ──────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold text-ink-50">Dashboard</h1>
          <p className="text-ink-400 mt-1 text-sm">Platform overview & analytics</p>
        </div>
        <div className="flex items-center gap-2">
          {/* range selector */}
          <div className="flex items-center bg-ink-900 border border-ink-700 rounded-xl p-1 gap-1">
            {rangeOptions.map((o) => (
              <button
                key={o.value}
                onClick={() => setRange(o.value)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  range === o.value
                    ? 'bg-accent-500 text-ink-950'
                    : 'text-ink-400 hover:text-ink-200'
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
          <Link to="/admin/influencers" className="btn-primary text-sm">
            + Influencer
          </Link>
        </div>
      </div>

      {error && (
        <div className="p-4 rounded-xl bg-danger-500/10 border border-danger-500/20 text-danger-400 text-sm">{error}</div>
      )}

      {/* ── KPI cards ────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
        <KpiCard
          label="Revenue"
          value={formatCurrency(summary.totalRevenue)}
          sub={range === 'all' ? 'all time' : `last ${range}`}
          accent={GOLD}
          loading={loading}
          icon={
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />
        <KpiCard
          label="Orders"
          value={summary.totalOrders.toLocaleString()}
          sub={range === 'all' ? 'all time' : `last ${range}`}
          accent={MINT}
          loading={loading}
          icon={
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
          }
        />
        <KpiCard
          label="Avg Order"
          value={formatCurrency(summary.avgOrderValue)}
          sub="per order"
          accent={ROYAL}
          loading={loading}
          icon={
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          }
        />
        <KpiCard
          label="Influencers"
          value={summary.totalInfluencers}
          sub={`${summary.activeInfluencers} active`}
          accent={BLUE}
          loading={loading}
          icon={
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          }
        />
        <KpiCard
          label="Live Sites"
          value={`${liveSites} / ${totalSites}`}
          sub="websites deployed"
          accent={ORANGE}
          loading={loading}
          icon={
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
            </svg>
          }
        />
        <KpiCard
          label="Vendors"
          value={summary.totalVendors}
          sub="on platform"
          accent={DANGER}
          loading={loading}
          icon={
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          }
        />
      </div>

      {/* ── Revenue chart + Pipeline donut ───────────────────────── */}
      <div className="grid lg:grid-cols-3 gap-4">

        {/* Revenue area chart */}
        <div className="lg:col-span-2 rounded-2xl bg-ink-900 border border-ink-700/50 p-5">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="text-sm font-semibold text-ink-100">Revenue & Orders</h2>
              <p className="text-xs text-ink-500 mt-0.5">Daily trend</p>
            </div>
            <div className="flex items-center gap-4 text-xs text-ink-400">
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: GOLD }} />Revenue</span>
              <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm" style={{ background: MINT }} />Orders</span>
            </div>
          </div>
          <div className="h-52">
            {loading ? (
              <div className="h-full rounded-xl bg-ink-800 animate-pulse" />
            ) : revenueByDay.length === 0 ? (
              <EmptyState text="No revenue data yet" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={revenueByDay} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
                  <defs>
                    <linearGradient id="gradRevenue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={GOLD} stopOpacity={0.25} />
                      <stop offset="95%" stopColor={GOLD} stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="gradOrders" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={MINT} stopOpacity={0.2} />
                      <stop offset="95%" stopColor={MINT} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" vertical={false} />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(v) => formatDate(v)}
                    tick={{ fill: '#525252', fontSize: 10 }}
                    axisLine={false}
                    tickLine={false}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    yAxisId="rev"
                    tickFormatter={(v) => formatCurrency(v)}
                    tick={{ fill: '#525252', fontSize: 10 }}
                    axisLine={false}
                    tickLine={false}
                    width={56}
                  />
                  <YAxis
                    yAxisId="ord"
                    orientation="right"
                    tick={{ fill: '#525252', fontSize: 10 }}
                    axisLine={false}
                    tickLine={false}
                    width={30}
                  />
                  <Tooltip content={<RevenueTooltip />} />
                  <Area yAxisId="rev" type="monotone" dataKey="revenue" stroke={GOLD} strokeWidth={2} fill="url(#gradRevenue)" dot={false} />
                  <Area yAxisId="ord" type="monotone" dataKey="orders" stroke={MINT} strokeWidth={2} fill="url(#gradOrders)" dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Website pipeline donut */}
        <div className="rounded-2xl bg-ink-900 border border-ink-700/50 p-5">
          <div className="mb-4">
            <h2 className="text-sm font-semibold text-ink-100">Website Pipeline</h2>
            <p className="text-xs text-ink-500 mt-0.5">{totalSites} total sites</p>
          </div>
          <div className="h-52">
            {loading ? (
              <div className="h-full rounded-xl bg-ink-800 animate-pulse" />
            ) : pipelineDonut.length === 0 ? (
              <EmptyState text="No sites created yet" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pipelineDonut}
                    cx="50%"
                    cy="45%"
                    innerRadius={52}
                    outerRadius={72}
                    paddingAngle={3}
                    dataKey="value"
                    stroke="none"
                  >
                    {pipelineDonut.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Legend
                    iconType="circle"
                    iconSize={7}
                    formatter={(value) => <span style={{ color: '#a8a8a8', fontSize: 11 }}>{value}</span>}
                  />
                  <Tooltip
                    contentStyle={{ background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: 12, fontSize: 12 }}
                    itemStyle={{ color: '#e8e8e8' }}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* ── Top Influencers + Product Requests ───────────────────── */}
      <div className="grid lg:grid-cols-2 gap-4">

        {/* Top influencers */}
        <div className="rounded-2xl bg-ink-900 border border-ink-700/50 p-5">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="text-sm font-semibold text-ink-100">Top Influencers</h2>
              <p className="text-xs text-ink-500 mt-0.5">by revenue</p>
            </div>
            <Link to="/admin/influencers" className="text-xs text-accent-400 hover:text-accent-300 transition-colors">
              View all →
            </Link>
          </div>
          <div className="h-52">
            {loading ? (
              <div className="h-full rounded-xl bg-ink-800 animate-pulse" />
            ) : influencerBars.length === 0 ? (
              <EmptyState text="No orders yet" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={influencerBars}
                  layout="vertical"
                  margin={{ top: 0, right: 8, bottom: 0, left: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" horizontal={false} />
                  <XAxis
                    type="number"
                    tickFormatter={(v) => formatCurrency(v)}
                    tick={{ fill: '#525252', fontSize: 10 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    tick={{ fill: '#a8a8a8', fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    width={80}
                  />
                  <Tooltip content={<InfluencerTooltip />} />
                  <Bar dataKey="revenue" radius={[0, 6, 6, 0]} maxBarSize={20}>
                    {influencerBars.map((_, i) => (
                      <Cell key={i} fill={GOLD} fillOpacity={1 - i * 0.15} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Product requests */}
        <div className="rounded-2xl bg-ink-900 border border-ink-700/50 p-5">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="text-sm font-semibold text-ink-100">Product Requests</h2>
              <p className="text-xs text-ink-500 mt-0.5">by status</p>
            </div>
            <Link to="/admin/catalog" className="text-xs text-accent-400 hover:text-accent-300 transition-colors">
              Manage →
            </Link>
          </div>
          <div className="h-52">
            {loading ? (
              <div className="h-full rounded-xl bg-ink-800 animate-pulse" />
            ) : productReqBars.length === 0 ? (
              <EmptyState text="No product requests yet" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={productReqBars}
                  layout="vertical"
                  margin={{ top: 0, right: 8, bottom: 0, left: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" horizontal={false} />
                  <XAxis
                    type="number"
                    tick={{ fill: '#525252', fontSize: 10 }}
                    axisLine={false}
                    tickLine={false}
                    allowDecimals={false}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    tick={{ fill: '#a8a8a8', fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    width={90}
                  />
                  <Tooltip
                    contentStyle={{ background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: 12, fontSize: 12 }}
                    itemStyle={{ color: '#e8e8e8' }}
                  />
                  <Bar dataKey="value" radius={[0, 6, 6, 0]} maxBarSize={20}>
                    {productReqBars.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* ── Quick actions ─────────────────────────────────────────── */}
      <div>
        <h2 className="text-xs font-medium text-ink-500 uppercase tracking-widest mb-3">Quick Actions</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            {
              to: '/admin/influencers',
              accent: GOLD,
              title: 'Influencers',
              sub: `${summary.totalInfluencers} total`,
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              ),
            },
            {
              to: '/admin/websites',
              accent: MINT,
              title: 'Website Manager',
              sub: `${liveSites} live`,
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                </svg>
              ),
            },
            {
              to: '/admin/catalog',
              accent: ROYAL,
              title: 'Product Catalog',
              sub: `${Object.values(productRequests).reduce((s, v) => s + v, 0)} requests`,
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              ),
            },
            {
              to: '/admin/creator-match',
              accent: BLUE,
              title: 'Creator Match',
              sub: 'AI matching',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              ),
            },
          ].map(({ to, accent, title, sub, icon }) => (
            <Link
              key={to}
              to={to}
              className="group rounded-2xl bg-ink-900 border border-ink-700/50 p-4 flex items-center gap-4 hover:border-ink-600 hover:bg-ink-800/80 transition-all"
            >
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-all group-hover:scale-110"
                style={{ background: `${accent}18`, color: accent }}
              >
                {icon}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-ink-100 group-hover:text-ink-50 transition-colors">{title}</p>
                <p className="text-xs text-ink-500 mt-0.5">{sub}</p>
              </div>
              <svg className="w-4 h-4 text-ink-600 group-hover:text-ink-400 ml-auto flex-shrink-0 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </Link>
          ))}
        </div>
      </div>

    </div>
  );
}
